import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo
} from 'react';
import { createPortal } from 'react-dom';
import { View, ActionButton, Text, Image } from '@adobe/react-spectrum';
import { Selection } from '@react-types/shared';
import {
  getCategoryIcon,
  categoryIcons
} from '../../../../utils/categoryIconUtils';
import styles from './ProductDropdown.module.scss';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import { useRBAC } from '../context/RBACContext';

// Search icon SVG component
const SearchIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.5303 17.4697L13.447 12.3865C14.4164 11.1851 15 9.66003 15 8C15 4.14062 11.8594 1 8 1C4.14062 1 1 4.14061 1 7.99999C1 11.8594 4.14062 15 8 15C9.66003 15 11.1851 14.4164 12.3865 13.447L17.4697 18.5303C17.6162 18.6767 17.8076 18.75 18 18.75C18.1924 18.75 18.3838 18.6768 18.5303 18.5303C18.8232 18.2373 18.8232 17.7627 18.5303 17.4697ZM8 13.5C4.96777 13.5 2.5 11.0322 2.5 7.99999C2.5 4.96776 4.96777 2.49999 8 2.49999C11.0322 2.49999 13.5 4.96776 13.5 7.99999C13.5 11.0322 11.0322 13.5 8 13.5Z"
      fill="#717171"
    />
  </svg>
);

// Clear icon SVG component
const ClearIcon = () => (
  <svg
    width="8"
    height="8"
    viewBox="0 0 8 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.95 1.05L5 4L7.95 6.95C8.05 7.05 8.05 7.25 7.95 7.35C7.85 7.45 7.65 7.45 7.55 7.35L4.6 4.4L1.65 7.35C1.55 7.45 1.35 7.45 1.25 7.35C1.15 7.25 1.15 7.05 1.25 6.95L4.2 4L1.25 1.05C1.15 0.95 1.15 0.75 1.25 0.65C1.35 0.55 1.55 0.55 1.65 0.65L4.6 3.6L7.55 0.65C7.65 0.55 7.85 0.55 7.95 0.65C8.05 0.75 8.05 0.95 7.95 1.05Z"
      fill="#292929"
    />
  </svg>
);

interface ProductDropdownProps {
  selectedProducts: string[];
  onSelectionChange: (products: string[]) => void;
  availableProducts?: string[];
  currentProductIcon?: React.ReactNode;
}

export const ProductDropdown: React.FC<ProductDropdownProps> = ({
  selectedProducts = [],
  onSelectionChange,
  availableProducts,
  currentProductIcon
}) => {
  // RBAC: Get permissions from context
  const permissions = useRBAC();
  const canEditGeneralInfo = permissions?.canEditGeneralInfo ?? false;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [productOptions, setProductOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [searchText, setSearchText] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const buttonRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  useEffect(() => {
    // Use availableProducts if provided, otherwise fall back to categoryIcons
    const products =
      availableProducts && availableProducts.length > 0
        ? availableProducts
        : Object.keys(categoryIcons);

    const options = products.map(product => ({
      value: product,
      label: product
    }));

    setProductOptions(options);
  }, [availableProducts, selectedProducts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDropdownOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        const dropdownPortal = document.querySelector(
          '[data-testid="product-dropdown-portal"]'
        );
        if (dropdownPortal && !dropdownPortal.contains(event.target as Node)) {
          setIsDropdownOpen(false);
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Clear search text when dropdown closes
  useEffect(() => {
    if (!isDropdownOpen) {
      setSearchText('');
      setHighlightedIndex(-1);
    }
  }, [isDropdownOpen]);

  // Track scroll position continuously
  useEffect(() => {
    const menuElement = menuRef.current;
    if (!menuElement) return;

    const handleScroll = () => {
      scrollPositionRef.current = menuElement.scrollTop;
    };

    menuElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => menuElement.removeEventListener('scroll', handleScroll);
  }, [isDropdownOpen]);

  // Preserve scroll position when selection changes - useLayoutEffect runs synchronously
  useLayoutEffect(() => {
    if (menuRef.current && scrollPositionRef.current > 0) {
      menuRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [selectedProducts]);

  const handleButtonClick = () => {
    // RBAC: Only allow dropdown to open if user can edit
    if (!canEditGeneralInfo) return;
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleProductSelect = (productValue: string) => {
    // Capture current scroll position
    const currentScroll = scrollPositionRef.current;

    let newSelection = [...selectedProducts];

    // Handle individual product selection
    if (selectedProducts.includes(productValue)) {
      // Don't allow deselection if it's the only selected product
      if (selectedProducts.length > 1) {
        newSelection = selectedProducts.filter(p => p !== productValue);
      } else {
        // Only one selected, don't deselect - just return
        return;
      }
    } else {
      newSelection = [...selectedProducts, productValue];
    }

    onSelectionChange(newSelection);

    // Restore scroll immediately after state update
    setTimeout(() => {
      if (menuRef.current) {
        menuRef.current.scrollTop = currentScroll;
      }
    }, 0);
  };

  // Filter products based on search text - memoized
  const filteredProductOptions = useMemo(() => {
    return productOptions.filter(option => {
      if (!searchText.trim()) return true;
      return option.label.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [productOptions, searchText]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredProductOptions]);

  // Memoize dropdown style to prevent unnecessary re-renders
  const dropdownStyle = useMemo(() => {
    if (!buttonRef.current) return {};
    const buttonRect = buttonRef.current.getBoundingClientRect();
    return {
      position: 'fixed' as const,
      top: buttonRect.bottom + 4,
      left: buttonRect.left,
      zIndex: 100000
      // minWidth: '200px',
      // maxWidth: '300px'
    };
  }, [isDropdownOpen]);

  const ProductDropdownPortal = () => {
    if (!isDropdownOpen || !buttonRef.current) return null;

    return createPortal(
      <div
        key="product-dropdown-portal-stable"
        data-testid="product-dropdown-portal"
        className={styles.dropdownPortal}
        style={dropdownStyle}
        onClick={e => e.stopPropagation()}
      >
        <div ref={menuRef} className={styles.productDropdownMenu}>
          {/* Search Field */}
          <div
            className={styles.searchFieldContainer}
            onClick={e => e.stopPropagation()}
          >
            <div className={styles.productSearchField}>
              <div className={styles.searchIconContainer}>
                <SearchIcon />
              </div>
              <input
                key="product-search-input"
                ref={searchInputRef}
                type="text"
                className={styles.productSearchInput}
                placeholder="Search product"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={e => {
                  e.stopPropagation();
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHighlightedIndex(prev => {
                      const next = Math.min(
                        prev + 1,
                        filteredProductOptions.length - 1
                      );
                      requestAnimationFrame(() => {
                        menuRef.current
                          ?.querySelector(`[data-option-index="${next}"]`)
                          ?.scrollIntoView({ block: 'nearest' });
                      });
                      return next;
                    });
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setHighlightedIndex(prev => {
                      const next = Math.max(prev - 1, 0);
                      requestAnimationFrame(() => {
                        menuRef.current
                          ?.querySelector(`[data-option-index="${next}"]`)
                          ?.scrollIntoView({ block: 'nearest' });
                      });
                      return next;
                    });
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (
                      highlightedIndex >= 0 &&
                      highlightedIndex < filteredProductOptions.length
                    ) {
                      handleProductSelect(
                        filteredProductOptions[highlightedIndex].value
                      );
                    }
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setIsDropdownOpen(false);
                    setHighlightedIndex(-1);
                  }
                }}
                autoFocus
                data-testid="product-search-input"
              />
              {searchText && (
                <button
                  className={styles.clearButton}
                  onClick={e => {
                    e.stopPropagation();
                    setSearchText('');
                    searchInputRef.current?.focus();
                  }}
                  aria-label="Clear search"
                  data-testid="product-search-clear"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                  >
                    <path
                      d="M5.23779 4L7.69384 1.54395C8.03565 1.20215 8.03565 0.647469 7.69384 0.305669C7.35204 -0.0361311 6.79834 -0.0361311 6.45654 0.305669L4 2.76221L1.54346 0.305659C1.20166 -0.0361311 0.647955 -0.0361311 0.306155 0.305659C-0.0356453 0.647449 -0.0356353 1.20215 0.306155 1.54395L2.7622 4L0.306155 6.45605C-0.0356453 6.79785 -0.0356453 7.35253 0.306155 7.69433C0.477055 7.86523 0.700685 7.95019 0.924805 7.95019C1.14892 7.95019 1.37255 7.86523 1.54345 7.69433L3.99999 5.23779L6.45653 7.69433C6.62743 7.86523 6.85106 7.95019 7.07518 7.95019C7.2993 7.95019 7.52293 7.86523 7.69383 7.69433C8.03563 7.35253 8.03563 6.79785 7.69383 6.45605L5.23779 4Z"
                      fill="#292929"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Product Options */}
          {filteredProductOptions.map((option, index) => {
            const isSelected = selectedProducts.includes(option.value);
            const isLastSelected = selectedProducts.length === 1 && isSelected;

            return (
              <div
                key={option.value}
                data-option-index={index}
                className={`${styles.productMenuItem} ${
                  isSelected ? styles.selected : ''
                } ${isLastSelected ? styles.disabled : ''}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleProductSelect(option.value)}
                style={{
                  cursor: isLastSelected ? 'not-allowed' : 'pointer',
                  ...(highlightedIndex === index
                    ? { backgroundColor: '#e9e9e9', borderRadius: '4px' }
                    : {})
                }}
              >
                <div className={styles.productMenuItemContent}>
                  <span>{option.label}</span>
                  {isSelected && (
                    <span className="menu-dropdown-check-icon">
                      <BlackCheckMarkIcon />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <View UNSAFE_className={styles.productDropdownContainer}>
      <div ref={buttonRef}>
        <ActionButton
          UNSAFE_className={styles.productIconButton}
          data-testid="slideout-header-product-icon-dropdown"
          aria-label="Select products"
          onPress={handleButtonClick}
          isDisabled={!canEditGeneralInfo}
          UNSAFE_style={{
            cursor: canEditGeneralInfo ? 'pointer' : 'not-allowed'
          }}
        >
          {currentProductIcon}
        </ActionButton>
      </div>
      {ProductDropdownPortal()}
    </View>
  );
};
